import { Box, Input } from '@mui/material';
import { MagnifyingGlass } from 'phosphor-react';
import StrategySteps from '../components/Strategies/StrategySteps';
import TaskList from '../components/Strategies/TaskList';
import DocumentsComponent from '../components/Strategies/DocumentsList';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Strategy } from '../Utilis/Types/Strategies';
import { StrategyService } from '../Service/StrategyService';
import { Loading } from '../components/Loading';
import { createNotification } from '../store/notifications/notification-slice';
import { useAppDispatch } from '../store/hooks';
import { DocumentService } from '../Service/DocumentService';
import { useModal } from '../context/ModalProvider/useModal';

const Strategies = () => {
  const { strategyId } = useParams();
  const [strategy, setStrategy] = useState<Strategy>();
  const [selectedStrategy, setSelectedStrategy] = useState<string>('');
  const [requiredDocuments, setRequiredDocuments] = useState<any[]>([]);
  const { closeModal, showLoader, hideLoader } = useModal();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const appDispatch = useAppDispatch();

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const fetchStrategies = StrategyService.getSingleStrategy(+strategyId!);
      const fetchDocuments = DocumentService.getAllUserDocuments();

      const fetches: any = [fetchStrategies];

      if (!requiredDocuments.length) {
        fetches.push(fetchDocuments);
      }

      const [strategyResult, documentsResult] = await Promise.all(fetches);

      setStrategy(strategyResult.data);
      setSelectedStrategy(strategyResult.data.steps[0]?.name);

      if (!!documentsResult) {
        setRequiredDocuments(documentsResult.data);
      }
    } catch (error) {
      appDispatch(
        createNotification({
          message: 'Something went wrong',
          type: 'error',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [strategyId]);

  const onDelete = async (id: string) => {
    try {
      await DocumentService.deleteDocument(id);

      const newRequiredDocuments = structuredClone(requiredDocuments);
      const requiredDocument = newRequiredDocuments.find(
        (req: any) => req.document?.id === id
      );

      requiredDocument.document = null;

      setRequiredDocuments(newRequiredDocuments);
      appDispatch(
        createNotification({
          message: 'Document has been deleted!',
          type: 'success',
        })
      );
    } catch (error) {
      appDispatch(
        createNotification({
          message: 'Something went wrong while deleting the document',
          type: 'error',
        })
      );
    }
  };

  const onAddFile = async (file: File, reqDocumentId: any) => {
    showLoader();
    try {
      const response: any = await DocumentService.addDocument({
        id: reqDocumentId,
        file,
      });

      const newRequiredDocuments = structuredClone(requiredDocuments);
      const requiredDocument = newRequiredDocuments.find(
        (req: any) => req.id === reqDocumentId
      );

      requiredDocument.document = response.data;
      setRequiredDocuments(newRequiredDocuments);

      appDispatch(
        createNotification({
          type: 'success',
          message: 'Document uploaded successfully!',
        })
      );
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Document upload failed!',
        })
      );
    } finally {
      hideLoader();
      closeModal();
    }
  };

  return (
    <Loading isLoading={isLoading}>
      <Box>
        <Input
          placeholder='Search'
          disableUnderline={true}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '16px',
            borderBottom: '1px solid #DBE0E2',
            backgroundColor: '#ffffff',
          }}
          onChange={e => setSearchQuery(e.target.value)}
          startAdornment={<MagnifyingGlass size={14} />}
        />

        <Box
          sx={{
            padding: '32px 32px 0 32px',
          }}
        >
          {!!strategy && (
            <>
              <StrategySteps
                setSelectedStrategy={setSelectedStrategy}
                strategy={strategy!}
                selectedStrategy={selectedStrategy}
              />
              <TaskList
                strategyStep={
                  strategy?.steps?.find(
                    (item: any) => item.name === selectedStrategy
                  )!
                }
                searchQuery={searchQuery}
              />
            </>
          )}
          <DocumentsComponent
            requiredDocuments={requiredDocuments}
            onAddFile={onAddFile}
            onDelete={onDelete}
          />
        </Box>
      </Box>
    </Loading>
  );
};

export default Strategies;
